import { Action, Module, VuexModule } from 'vuex-class-modules'
import store from '@/store'
import { TripModificationStep } from '@/models/TripModification'
import {
  TripModificationTrip as TripModificationTripDTO,
  ModifiedTrip as ModifiedTripDTO,
  TripEstimationRequest,
  TripModificationPaymentUpdate
} from '@/models/dto'
import { TripModificationTrip } from '@/classes/TripModificationTrip'
import footer from '@/store/modules/footer'
import { WizardStore } from '../WizardStore'
import service from '@/services/tripModification'
import quotes from '@/services/quotes'
import { TripModificationStepKey } from '@/utils/enum'
import { WizardVehicle } from '@/models/dto'
import { roundSecondsToNearestXMinutes } from '@/utils/datetime'
import { estimateDropoffDatetime } from '@/utils/stop'
import { DateTime } from 'luxon'

const SELF_SERVE_DRIVE_TIME_MULTIPLIER = 1.1
const MINIMUM_TRAVEL_TIME = 15
@Module({ generateMutationSetters: true })
class TripModificationModule extends VuexModule
  implements WizardStore<TripModificationStep> {
  _managedId: string = null
  _routeName: string = null
  _currentTrip: TripModificationTripDTO = null
  _requestedTrip: TripModificationTripDTO = null
  _requestedTripForReview: ModifiedTripDTO = null
  _tripModificationId: number = null
  _isTripModificationApplied: boolean = false
  _isOpen = false
  _step: TripModificationStep = null
  _steps: TripModificationStep[] = this.computedSteps
  _forwardButtonDisabled = false
  _paymentUpdate: TripModificationPaymentUpdate
  _newCustomerPaymentProfileId: number = null
  _isDraftInProgress: boolean = false
  _isDraftCustomerReview: boolean = false

  /**
   * Gets the steps.
   * @returns The steps.
   */
  get steps(): TripModificationStep[] {
    return this._steps
  }

  /**
   * Gets the current step.
   * @returns The current step.
   */
  get step(): TripModificationStep {
    return this._step
  }

  /**
   * Gets the total number of steps.
   * @returns The total number of steps.
   */
  get stepCount(): number {
    return this._steps.length
  }

  /**
   * Gets the current step index.
   * @returns The current step index.
   */
  get currentStepIndex(): number {
    return this.steps.findIndex(({ key }) => key === this.step.key)
  }

  get isOpen(): boolean {
    return this._isOpen
  }

  get managedId(): string {
    return this._managedId
  }

  get routeName(): string {
    return this._routeName
  }

  get currentTrip(): TripModificationTripDTO {
    return this._currentTrip
  }

  get requestedTrip(): TripModificationTripDTO {
    return this._requestedTrip
  }

  get paymentUpdate(): TripModificationPaymentUpdate {
    return this._paymentUpdate
  }

  get requestedTripForReview(): ModifiedTripDTO {
    return this._requestedTripForReview
  }

  get tripModificationId(): number {
    return this._tripModificationId
  }

  get isTripModificationApplied(): boolean {
    return this._isTripModificationApplied
  }

  get forwardButtonDisabled(): boolean {
    return this._forwardButtonDisabled
  }

  get isDraftInProgress(): boolean {
    return this._isDraftInProgress
  }

  get isDraftCustomerReview(): boolean {
    return this._isDraftCustomerReview
  }

  get computedSteps(): TripModificationStep[] {
    return [
      {
        component: () => import('@/components/TripModificationItinerary.vue'),
        key: TripModificationStepKey.Itinerary,
        orderIndex: 0,
      },
      {
        component: () => import('@/components/TripModificationVehicle.vue'),
        key: TripModificationStepKey.Vehicle,
        orderIndex: 1,
      },
      {
        component: () => import('@/components/TripModificationReview.vue'),
        key: TripModificationStepKey.Review,
        orderIndex: 2,
      },
      {
        component: () => import('@/components/TripModificationBalanceUpdateReview.vue'),
        key: TripModificationStepKey.BalanceUpdateReview,
        orderIndex: 3,
        excludeFromStepCount: true,
      },
      {
        component: this._isTripModificationApplied ? () => import('@/components/TripModificationApplication.vue') : () => import('@/components/TripModificationConfirmation.vue'),
        key: this._isTripModificationApplied ? TripModificationStepKey.Application : TripModificationStepKey.Confirmation,
        orderIndex: 4,
        excludeFromStepCount: true,
      },
      {
        component: () => import('@/components/TripModificationError.vue'),
        key: TripModificationStepKey.SubmissionError,
        orderIndex: 5,
        excludeFromStepCount: true,
      },
    ]
  }

  get customerReviewSteps(): TripModificationStep[] {
    return [
      {
        component: () => import('@/components/TripModificationReview.vue'),
        key: TripModificationStepKey.Review,
        orderIndex: 0,
      },
      {
        component: () => import('@/components/TripModificationBalanceUpdateReview.vue'),
        key: TripModificationStepKey.BalanceUpdateReview,
        orderIndex: 1
      }
    ]
  }

  @Action
  setIsOpen(value: boolean): void {
    this._isOpen = value
    this._step = this._steps[0]
    footer.setShow(!value)
  }

  @Action
  setSteps(steps: TripModificationStep[]): void {
    this._steps = steps
  }

  @Action
  setStep(step: TripModificationStep): void {
    this._step = step
  }

  @Action
  setManagedId(id: string): void {
    this._managedId = id
  }

  @Action
  setRouteName(name: string): void {
    this._routeName = name
  }

  @Action
  async nextStep(): Promise<void> {
    if (this.currentStepIndex === this.steps.length - 1) {
      return
    }
    const nextStep = this.steps[this.currentStepIndex + 1]
    this.setStep(nextStep)
  }

  @Action
  previousStep(): void {
    if (this.currentStepIndex === 0) {
      return
    }
    const previousStep = this.steps[this.currentStepIndex - 1]
    this.setStep(previousStep)
  }

  @Action
  setStepByKey(key: TripModificationStepKey): void {
    if (!this.steps) {
      return
    }
    const matchingStep = this.steps.find(step => step.key === key)
    if (matchingStep) {
      this.setStep(matchingStep)
    }
  }

  @Action
  setRequestedAda(ada: boolean): void {
    this._requestedTrip.ada = ada
  }

  @Action
  setRequestedSpab(spab: boolean): void {
    this._requestedTrip.spab = spab
  }

  @Action
  setRequestedPassengerCount(passengerCount: number): void {
    this._requestedTrip.passengerCount = passengerCount
  }

  @Action
  setRequestedVehicles(vehicles: WizardVehicle[]): void {
    this._requestedTrip.vehicles = vehicles
  }

  @Action
  setNewCustomerPaymentProfileId(newCustomerPaymentProfileId: number): void {
    this._newCustomerPaymentProfileId = newCustomerPaymentProfileId
  }

  @Action
  setForwardButtonDisabled(forwardButtonDisabled: boolean): void {
    this._forwardButtonDisabled = forwardButtonDisabled
  }

  @Action
  setIsDraftInProgress(isDraftInProgress: boolean): void {
    this._isDraftInProgress = isDraftInProgress
  }

  @Action
  setIsDraftCustomerReview(isDraftCustomerReview: boolean): void {
    this._isDraftCustomerReview = isDraftCustomerReview
  }

  /**
   * Starts trip modification process by fetching itinerary information. Upon
   * success, the current trip is set to the fetched trip.
   *
   * @param managedId - The reservation managed ID to use to get the trip itinerary information.
   * @returns nothing.
   */
  @Action
  async start(): Promise<void> {
    if (!this._managedId) {
      return
    }

    try {
      const res = await service.start(this._managedId)
      const trip = new TripModificationTrip(res.data)
      this._currentTrip = trip
      this._requestedTrip = trip

      const requestedTripStops = this._requestedTrip.stops
      // set all the travel times for the requested trip
      if (requestedTripStops.length > 0) {
        requestedTripStops[0].travelTimeFromPreviousStopInSeconds = 0
      }
      for (let i = 1; i < requestedTripStops.length; i++) {
        const previousStop = requestedTripStops[i - 1]
        const currentStop = requestedTripStops[i]

        const previousPickupTime = DateTime.fromISO(previousStop.pickupDatetime)
        const currentDropoffTime = DateTime.fromISO(currentStop.dropoffDatetime)

        const travelDiff = currentDropoffTime.diff(previousPickupTime, 'seconds')
        const travelTimeFromPreviousStopInSeconds = travelDiff.seconds
        currentStop.travelTimeFromPreviousStopInSeconds = travelTimeFromPreviousStopInSeconds
      }
    } catch (error: any) {
      console.error(error)
      if (error.response.status === 400 &&
        error.response.data.message === 'Trip modification draft already exists for this reservation'
      ) {
        this.setIsDraftInProgress(true)
      }
    }
  }

  @Action
  async startDraftCustomerReview(): Promise<void> {
    if (!this._managedId) {
      return
    }

    try {
      const res = await service.fetchTripModificationDraftForReview(this._managedId)
      this._currentTrip = res.data.currentTrip
      this._requestedTripForReview = res.data.requestedTrip
      this._paymentUpdate = res.data.paymentUpdate
      this.setSteps(this.customerReviewSteps)
      this.setStep(this.steps[0])
    } catch (error: any) {
      console.error(error)
    }
  }

  @Action
  async reviewModification(): Promise<void> {
    if (!this._managedId) {
      return
    }

    try {
      const payload = {
        managedId: this._managedId,
        requestedTrip: this._requestedTrip,
      }
      const res = await service.review(payload)
      this._currentTrip = res.data.currentTrip
      this._requestedTripForReview = res.data.requestedTrip
      this._paymentUpdate = res.data.paymentUpdate
      this.setSteps(this.computedSteps)
    } catch (error: any) {
      if (error.response.status === 400 &&
        error.response.data.message === 'Trip modification draft already exists for this reservation'
      ) {
        this.setIsDraftInProgress(true)
      } else {
        this.setForwardButtonDisabled(true)
      }
      console.error(error)
    }
  }

  /**
   * Submits or applies modification request. The request will either be auto-applied if eligible
   * or a trip modification request will be created for manual review if not.
   *
   * @param managedId - The reservation managed ID to use to get the trip itinerary information.
   * @param requestedTrip - The requested trip modifications.
   * @returns nothing.
   */
  @Action
  async submitModification(): Promise<void> {
    if (!this._managedId) {
      return
    }

    try {
      const requestedTrip = new TripModificationTrip(
        this._requestedTripForReview
      )
      const res = await service.confirm({
        managedId: this._managedId,
        requestedTrip,
        priceDifference: this._paymentUpdate?.priceDifference,
        newCustomerPaymentProfileId: this._newCustomerPaymentProfileId
      })
      this._tripModificationId = res.data.tripModificationRequestId
      this._isTripModificationApplied = res.data.changesAutoApplied
      this.setSteps(this.computedSteps)
    } catch (error: any) {
      if (error.response.status === 400 &&
        error.response.data.message === 'Trip modification draft already exists for this reservation'
      ) {
        this.setIsDraftInProgress(true)
      }
      this.setStepByKey(TripModificationStepKey.SubmissionError)
      console.error(error)
    }
  }

  /**
   * Loads an existing trip modification request.
   *
   * @param managedId - The reservation managed ID to use to get the trip modification request information.
   * @returns nothing.
   */
  @Action
  async getPendingModification(): Promise<void> {
    if (!this._managedId) {
      return
    }

    try {
      const res = await service.success(this._managedId)
      this._currentTrip = res.data.currentTrip
      this._requestedTripForReview = res.data.requestedTrip
      this._paymentUpdate = res.data.paymentUpdate
    } catch (error: any) {
      if (error.response.status === 400 &&
        error.response.data.message === 'Trip modification draft already exists for this reservation'
      ) {
        this.setIsDraftInProgress(true)
      }
      console.error(error)
    }
  }


  /**
   * Re-estimate every stop in the trip starting at the provided stop index.
   * @param stopIndex - The stop to start the estimate from
   * @returns
   */
  @Action
  async estimateTrip(stopIndex: number): Promise<void> {
    if (!this._requestedTrip) {
      return
    }

    try {
      const payload = this.getTripEstimateRequest()
      const response = await quotes.tripEstimation(payload)
      const tripEstimate = response?.data?.[0]
      if (!tripEstimate) {
        console.warn('Unable to estimate trip')
        return
      }

      const stopSlice = this._requestedTrip.stops.slice(stopIndex)

      for (const stop of stopSlice) {
        const secondsFromPreviousStop =
          tripEstimate.timesFromPreviousStop[stop.orderIndex]
        const travelTimeFromPreviousStopInSeconds = this.calculateTravelTime(
          secondsFromPreviousStop
        )
        stop.travelTimeFromPreviousStopInSeconds = travelTimeFromPreviousStopInSeconds

        const dropoffDatetime = this.calculateDropoffDatetime(
          stop.orderIndex,
          travelTimeFromPreviousStopInSeconds,
          stop.address?.zoneId
        )
        stop.dropoffDatetime = dropoffDatetime
      }
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Clear the estimate dropoff related fields when we know they aren't valid.
   * Primarily used when a new stop is added and the remaining estimate fields are
   * rendered invalid.
   * @param stopIndex the index to start wiping estimated values from
   */
  @Action
  async clearEstimatedValues(stopIndex: number): Promise<void> {
    if (!this._requestedTrip) {
      return
    }

    try {
      const stopSlice = this._requestedTrip.stops.slice(stopIndex)
      for (const stop of stopSlice) {
        stop.travelTimeFromPreviousStopInSeconds = 0
        stop.dropoffDatetime = null
      }
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Reset the module state when the wizard modal closes. Ensures a clean state every time
   * the wizard is opened.
   */
  @Action
  async resetState(): Promise<void> {
    this._managedId = null
    this._routeName = null
    this._currentTrip = null
    this._requestedTrip = null
    this._requestedTripForReview = null
    this._tripModificationId = null
    this._isTripModificationApplied = false
    this._isOpen = false
    this._step = null
    this._steps = this.computedSteps
    this._forwardButtonDisabled = false
    this._paymentUpdate = null
    this._newCustomerPaymentProfileId = null
  }

  getTripEstimateRequest(): TripEstimationRequest {
    return {
      trips: [
        {
          stops: this._requestedTrip.stops.map((stop) => {
            return {
              address: {
                name: stop.address?.name,
                country: stop.address?.country,
                lat: stop.address?.lat,
                lng: stop.address?.lng,
                zoneId: stop.address?.zoneId,
              },
              active: true,
              orderIndex: stop.orderIndex,
              pickupDatetime: stop.pickupDatetime,
            }
          }),
          vehicles: this._requestedTrip.vehicles.map(({ vehicleTypeKey }) => ({
            vehicleTypeKey,
          })),
        },
      ],
    }
  }

  calculateTravelTime(secondsFromPreviousStop: number): number {
    if (secondsFromPreviousStop === null) {
      return null
    }
    const duration = Math.max(
      secondsFromPreviousStop * SELF_SERVE_DRIVE_TIME_MULTIPLIER,
      MINIMUM_TRAVEL_TIME
    )
    return roundSecondsToNearestXMinutes(duration, MINIMUM_TRAVEL_TIME)
  }

  calculateDropoffDatetime(
    stopIndex: number,
    travelTimeFromPreviousStopInSeconds: number,
    zoneId: string
  ): string | null {
    if (stopIndex === 0) {
      return null
    }
    const previousStopPickupTime = this._requestedTrip?.stops[stopIndex - 1]
      .pickupDatetime
    const estimatedDropoffDatetime = estimateDropoffDatetime(
      previousStopPickupTime,
      travelTimeFromPreviousStopInSeconds,
      zoneId
    )
    return estimatedDropoffDatetime?.toISO() || null
  }

}

export default new TripModificationModule({ store, name: 'tripModification' })
